import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Printer } from "lucide-react";

import { STAGES, groupRounds, stageLabel, stageOf } from "../components/tournament/BracketTab";
import { BrandLogo, SponsorStrip } from "../components/tournament/Brand";
import { fetchPublicTournament, tournaments } from "../lib/tournamentApi";

/** Director printables: bracket poster (letter / 18×24, blank or filled) and QR poster. */
export default function TournamentPrint() {
  const { id, kind } = useParams();
  const [q] = useSearchParams();
  const [data, setData] = useState(null);
  const [slug, setSlug] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    tournaments.get(id).then(async (w) => {
      setSlug(w.tournament.slug);
      setData(await fetchPublicTournament(w.tournament.slug));
    }).catch((e) => setError(e.message));
  }, [id]);
  if (error) return <div style={{ padding: 24 }} data-testid="print-error">{error}</div>;
  if (!data) return <div style={{ padding: 24 }}>Loading…</div>;
  const poster = q.get("size") === "poster";
  const page = kind === "qr" ? "size: letter portrait; margin: 0.5in" : poster ? "size: 24in 18in; margin: 0.6in" : "size: letter landscape; margin: 0.4in";
  return (
    <div data-testid={`print-${kind}`} style={{ background: "#fff", color: "#111", minHeight: "100vh", fontFamily: "Barlow, system-ui, sans-serif",
                                                 fontSize: poster ? 20 : 11, padding: 24 }}>
      <style>{`@page { ${page} } @media print { .no-print { display: none !important; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }`}</style>
      <button className="no-print" data-testid="print-now" onClick={() => window.print()}
              style={{ position: "fixed", right: 20, top: 20, display: "inline-flex", gap: 6, alignItems: "center", background: "#1C2B1E", color: "#F5F0E8",
                       border: "none", borderRadius: 999, padding: "10px 18px", fontWeight: 800, cursor: "pointer", zIndex: 10 }}>
        <Printer size={16} aria-hidden="true" /> Print / Save PDF</button>
      {kind === "qr" ? <QrPoster data={data} slug={slug} />
        : <BracketPoster data={data} divisionId={q.get("division")} fill={q.get("fill") !== "0"} poster={poster} />}
    </div>
  );
}

function Header({ data, sub, big }) {
  return (
    <div style={{ display: "flex", gap: 16, alignItems: "center", borderBottom: "2px solid #1C2B1E", paddingBottom: 10, marginBottom: 16 }}>
      <BrandLogo branding={data.branding} size={big ? 110 : 54} testId="print-logo" />
      <div>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: big ? "3.4em" : "2.6em", lineHeight: 1 }}>{data.tournament.name}</div>
        <div style={{ opacity: 0.7, fontSize: "1.1em" }}>{sub}</div>
      </div>
    </div>
  );
}

function Footer({ data }) {
  return (
    <div style={{ marginTop: 20, display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16 }}>
      <SponsorStrip branding={data.branding} tile={40} testId="print-sponsors" />
      <div style={{ fontSize: "0.9em", letterSpacing: "0.14em", opacity: 0.6, whiteSpace: "nowrap" }}>POWERED BY CHALKOS</div>
    </div>
  );
}

function BracketPoster({ data, divisionId, fill, poster }) {
  const division = data.divisions.find((d) => d.id === divisionId) || data.divisions[0];
  const games = data.games.filter((g) => g.division_id === division.id);
  const names = Object.fromEntries(data.teams.map((x) => [x.id, x.name]));
  const stages = STAGES.map(([k]) => k).filter((k) => k !== "pool" && games.some((g) => stageOf(g) === k));
  return (
    <>
      <Header data={data} sub={`${division.name} · ${fill ? "Bracket" : "Blank bracket"}`} big={poster} />
      {division.standings && fill && division.standings.map((p) => (
        <div key={p.pool_id} style={{ display: "inline-block", verticalAlign: "top", marginRight: 24, marginBottom: 12 }}>
          <b>{data.pools.find((x) => x.id === p.pool_id)?.name || p.name}</b>
          {p.rows.map((r) => <div key={r.team_id}>{r.place}. {names[r.team_id]} ({r.w}-{r.l}{r.t ? `-${r.t}` : ""})</div>)}
        </div>
      ))}
      {!stages.length && <div data-testid="print-bracket-empty" style={{ opacity: 0.6 }}>Bracket hasn't been built yet.</div>}
      {stages.map((k) => (
        <div key={k} style={{ marginBottom: 18, breakInside: "avoid" }}>
          {stages.length > 1 && <div style={{ fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{stageLabel(k, division)}</div>}
          <Columns games={games.filter((g) => stageOf(g) === k)} names={names} fill={fill} />
        </div>
      ))}
      <Footer data={data} />
    </>
  );
}

function Columns({ games, names, fill }) {
  const { rounds, roundKeys } = groupRounds(games);
  const first = roundKeys[0];
  return (
    <div style={{ display: "flex", gap: "1.6em", alignItems: "stretch" }}>
      {roundKeys.map((r) => (
        <div key={r} style={{ display: "flex", flexDirection: "column", justifyContent: "space-around", minWidth: "11em", flex: 1 }}>
          {rounds[r].filter((g) => !g.is_bye || fill).map((g) => {
            const show = (tid) => (fill || r === first ? names[tid] || (g.is_bye ? "Bye" : "") : "");
            return (
              <div key={g.id} style={{ border: "1px solid #1C2B1E", borderRadius: 4, margin: "0.3em 0" }}>
                {[["home", g.home_team_id, g.home_score], ["away", g.away_team_id, g.away_score]].map(([side, tid, sc]) => (
                  <div key={side} style={{ display: "flex", justifyContent: "space-between", padding: "0.25em 0.5em", minHeight: "1.4em",
                                           borderTop: side === "away" ? "1px solid #ccc" : "none",
                                           fontWeight: fill && g.winner_team_id && g.winner_team_id === tid ? 800 : 400 }}>
                    <span>{show(tid)}</span><span>{fill && sc != null ? sc : ""}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function QrPoster({ data, slug }) {
  const url = `${window.location.origin}/t/${slug}`;
  return (
    <div style={{ textAlign: "center", maxWidth: "7.5in", margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}><BrandLogo branding={data.branding} size={110} testId="qr-logo" /></div>
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 64, lineHeight: 1 }}>{data.tournament.name}</div>
      <div style={{ fontSize: 26, fontWeight: 800, marginTop: 14 }}>Live scores + alerts</div>
      <div style={{ fontSize: 18, opacity: 0.7, marginTop: 4 }}>Scan to follow your team. Brackets, locations and announcements, live.</div>
      <div data-testid="qr-code" style={{ display: "inline-block", padding: 18, border: "3px solid #1C2B1E", borderRadius: 18, marginTop: 24 }}>
        <QRCodeSVG value={url} size={340} level="M" />
      </div>
      <div data-testid="qr-url" style={{ fontSize: 18, marginTop: 12, fontWeight: 700 }}>{url.replace(/^https?:\/\//, "")}</div>
      <div style={{ marginTop: 30, display: "flex", justifyContent: "center" }}><SponsorStrip branding={data.branding} tile={44} testId="qr-sponsors" /></div>
      <div style={{ marginTop: 22, fontSize: 12, letterSpacing: "0.14em", opacity: 0.6 }}>POWERED BY CHALKOS</div>
    </div>
  );
}
